import React from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import Image from 'next/image'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import Accordion from '../src/components/Accordion'
import ExchangeGuide from '../src/components/Product/exchanges/crypto/ExchangeGuide'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
}))

const howCryptoExchangesWork = [
  {
    summary: 'Register for free',
    details:
      'Sign up for any exchange listed on our website. All you need is an email address and password.',
    action: {
      label: 'Compare crypto exhanges',
      link: urls.exchanges,
    },
  },
  {
    summary: 'Verify your identity',
    details:
      'Upload digital copies of your ID documents and proof-of-address. Most exchanges need this to comply with regulation.',
  },
  {
    summary: 'Fund your account',
    details:
      'Load fiat currency ($, € etc.) into your exchange account by sending via bank transfer or per credit card.',
  },
  {
    summary: 'Buy crypto',
    details:
      'Once the money is in your account you can simply buy any cryptocurrency that you want in one simple button click.',
  },
]

const howP2PExchangesWork = [
  {
    summary: 'Register for free',
    details: 'Sign up for the exchange by providing an e-mail and password.',
  },
  {
    summary: 'Verify your identity',
    details:
      'Some but not all peer-to-peer exchanges will oblige you to verify your identity before you can buy or sell. On some it’s optional but individual sellers might require it.',
  },
  {
    summary: 'Find an offer',
    details:
      'Browse through the marketplace and look for sell offers that suit you. Look for price, payment methods and seller reputation.',
  },
  {
    summary: 'Contact the seller',
    details:
      'Make a request to the seller. Once they accept the marketplace opens an escrow account where you can send the money to.',
  },
  {
    summary: 'Transfer funds',
    details:
      'Transfer the payment into the secure escrow account. Once the payment has arrived the escrow account with release the crypto you have purchased into your wallet.',
  },
]

const howDExchangesWork = [
  {
    summary: 'Get an Ethereum wallet',
    details:
      'Download any of the free Ethereum wallets listed on Cryptotesters. You need a wallet to use any decentralized application.',
    action: {
      label: 'Compare ethereum wallets',
      link: urls.ethereumWallets,
    },
  },
  {
    summary: 'Send some ETH to your wallet',
    details:
      'Send some Ether (ETH) from an exchange to your wallet. You need ETH to pay for transaction fees on the blockchain.',
  },
  {
    summary: 'Navigate to the exchange’s website',
    details:
      'Every decentralized application has one (or multiple) user interfaces for users to use.',
  },
  {
    summary: 'Connect your Ethereum wallet',
    details:
      'Connect your wallet by scanning a QR-code or clicking a “connect” button. This varies depending on whether you have a mobile wallet or a browser wallet.',
  },
  {
    summary: 'Make your trade',
    details:
      'Select the tokens you want to buy and execute the trade by signing with your wallet. There will also be a network transaction fee included that goes to miners.',
  },
]

const Exchanges = (): JSX.Element => {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Exchanges`}
            description={`Cryptotesters.com.`}
            openGraph={{
              url: `https://cryptotesters.com`,
              title: `Cryptotesters.com - Exchanges`,
              description: `EXchanges Cryptotesters.com.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 145,
                  height: 56,
                  alt: `Cryptotesters.com`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">Choosing the best exchange</Typography>
                  <Typography variant="body2">
                    New users often struggle with figuring out how to buy their first crypto, but it
                    isn’t as difficult as people make it seem. There are a handful of world-class
                    crypto exchanges to pick from.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href={'/best-cryptocurrency-exchanges'}
                    className={classes.heroCTA}
                  >
                    Compare Exchanges
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <iframe
                        width="560"
                        height="315"
                        src="https://www.youtube.com/embed/GE1-FK1-YFw"
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">Crypto Exchanges</Typography>
                <Typography variant="body2">
                  Crypto exchanges allow you to buy crypto at the best market rate using
                  conventional payment methods like credit cards or bank transfer. They are gateways
                  to connect the old financial system with the new world of cryptocurrencies.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.exchanges}
                  className={classes.heroCTA}
                >
                  Compare Crypto exchanges
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: Crypto exchanges
                </Button> */}
                <Typography variant="h3">How do crypto exchanges work?</Typography>
                <Accordion steps={howCryptoExchangesWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/exchanges/crypto-exchanges.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">Peer-to-peer exchanges</Typography>
                <Typography variant="body2">
                  Peer-to-peer exchanges allow you to buy cryptocurrencies from private sellers.
                  They are similar to marketplaces like eBay in the traditional world where sellers
                  can set whatever price and payment method they want.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.p2p}
                  className={classes.heroCTA}
                >
                  Compare peer-to-peer exchanges
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: Peer-to-peer exchanges
                </Button> */}
                <Typography variant="h3">How do crypto peer-to-peer exchanges work?</Typography>
                <Accordion steps={howP2PExchangesWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/exchanges/p2p-exchanges.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <Grid container className={classes.section}>
              <Grid item md={6} xs={12}>
                <Typography variant="h1">Decentralized exchanges</Typography>
                <Typography variant="body2">
                  Decentralized exchanges allow you to trade directly on the Ethereum Blockchain
                  from your Ethereum wallet retaining 100% control over your funds. Swaps are
                  automated by so-called “smart contracts” without any intermediary involved.
                  <br />
                  Get any of the Ethereum wallets that we are suggesting on this site. Transfer
                  ether (ETH) from an exchange to that wallet to pay for transaction fees.
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  href={urls.decentralizedExchanges}
                  className={classes.heroCTA}
                >
                  Compare decentralized exchanges
                </Button>
                {/* <Button color="inherit" href="#" variant="text" className={classes.heroCTA}>
                  Video: Decentralized exchanges
                </Button> */}
                <Typography variant="h3">How do crypto Decentralized exchanges work?</Typography>
                <Accordion steps={howDExchangesWork} />
              </Grid>

              <Grid item md={6} xs={12}>
                <div className={classes.videoWrapper}>
                  <Image src="/exchanges/dexchange.png" objectFit="contain" layout="fill" />
                </div>
              </Grid>
            </Grid>

            <div>
              <ExchangeGuide />
              <FAQSection />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </>
      )}
    </Amplitude>
  )
}

export default Exchanges
